import type { SeriesMarker } from 'lightweight-charts';
import { formatPriceForSymbol } from './priceFormat';

export type ExecutionSeriesMarker = SeriesMarker<number> & {
  executionId?: string;
  executionTime?: number;
  executionPrice?: number;
  fullText?: string;
  side?: 'buy' | 'sell';
  actionType?: 'open' | 'scaleIn' | 'scaleOut' | 'close' | 'none';
};

type CandleLike = {
  time?: number | string;
  open?: number | string;
  high?: number | string;
  low?: number | string;
  close?: number | string;
  o?: number | string;
  h?: number | string;
  l?: number | string;
  c?: number | string;
};

type ExecutionLike = {
  id?: string | number;
  storageId?: string | number;
  executionId?: string | number;
  orderId?: string | number;
  timestamp?: number | string | Date;
  time?: number | string | Date;
  executedAt?: number | string | Date;
  date?: number | string | Date;
  t?: number | string | Date;
  isEntry?: boolean;
  isExit?: boolean;
  isOpen?: boolean;
  isClose?: boolean;
  side?: string;
  action?: string;
  type?: string;
  qty?: number | string;
  quantity?: number | string;
  shares?: number | string;
  price?: number | string;
  avgPrice?: number | string;
};

const REVERSAL_COLOR = '#f59e0b';
const BUY_COLOR = '#16a34a';
const SELL_COLOR = '#dc2626';
const SCALE_IN_BUY_COLOR = '#4ade80';
const SCALE_IN_SELL_COLOR = '#f87171';

const toUnixSeconds = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1_000_000_000_000 ? Math.floor(value / 1000) : Math.floor(value);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const asNum = Number(value);
    if (Number.isFinite(asNum)) {
      return asNum > 1_000_000_000_000 ? Math.floor(asNum / 1000) : Math.floor(asNum);
    }
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return Math.floor(parsed / 1000);
  }

  if (value instanceof Date) {
    const ts = value.getTime();
    return Number.isFinite(ts) ? Math.floor(ts / 1000) : null;
  }

  return null;
};

const normalizeSide = (value: unknown): 'buy' | 'sell' | null => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw.includes('buy') || raw === 'b') return 'buy';
  if (raw.includes('sell') || raw === 's') return 'sell';
  return null;
};

const toPositiveNumber = (value: unknown): number => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return 1;
  return Math.max(1, Math.abs(num));
};

function inferTfSecFromCandles(candleTimes: number[]): number {
  if (!Array.isArray(candleTimes) || candleTimes.length < 2) return 60;
  let minDiff = Number.POSITIVE_INFINITY;
  for (let i = 1; i < candleTimes.length; i += 1) {
    const diff = Math.abs(candleTimes[i] - candleTimes[i - 1]);
    if (Number.isFinite(diff) && diff > 0) minDiff = Math.min(minDiff, diff);
  }
  return Number.isFinite(minDiff) && minDiff > 0 ? minDiff : 60;
}

export function normalizeExecutionsToMarkers(
  executions: ExecutionLike[],
  candles: CandleLike[],
  options?: { symbol?: string; secType?: string },
): ExecutionSeriesMarker[] {
  if (!Array.isArray(executions) || executions.length === 0) return [];
  if (!Array.isArray(candles) || candles.length === 0) return [];

  const candleTimes = candles
    .map((c) => toUnixSeconds(c?.time))
    .filter((t): t is number => Number.isFinite(t))
    .sort((a, b) => a - b);

  const candleByTime = new Map<number, { low: number; high: number }>();
  for (const candle of candles) {
    const t = toUnixSeconds(candle?.time);
    if (t == null) continue;
    const low = Number((candle as any)?.low ?? (candle as any)?.l);
    const high = Number((candle as any)?.high ?? (candle as any)?.h);
    if (!Number.isFinite(low) || !Number.isFinite(high)) continue;
    candleByTime.set(t, { low: Math.min(low, high), high: Math.max(low, high) });
  }

  if (candleTimes.length === 0) return [];

  const sortedExecutions = executions
    .map((exec, index) => ({ exec, index }))
    .sort((a, b) => {
      const ta = toUnixSeconds(a.exec?.timestamp ?? a.exec?.time ?? a.exec?.executedAt ?? a.exec?.date ?? a.exec?.t) ?? Number.MAX_SAFE_INTEGER;
      const tb = toUnixSeconds(b.exec?.timestamp ?? b.exec?.time ?? b.exec?.executedAt ?? b.exec?.date ?? b.exec?.t) ?? Number.MAX_SAFE_INTEGER;
      if (ta === tb) return a.index - b.index;
      return ta - tb;
    });

  const tfSec = inferTfSecFromCandles(candleTimes);
  const minCandle = candleTimes[0];
  const maxCandle = candleTimes[candleTimes.length - 1];

  const duplicateCounts = new Map<string, number>();
  const markers: ExecutionSeriesMarker[] = [];
  let runningPosition = 0;

  for (const { exec } of sortedExecutions) {
    const side = normalizeSide(exec?.side ?? exec?.action ?? exec?.type);
    if (!side) continue;

    const tsSec = toUnixSeconds(exec?.timestamp ?? exec?.time ?? exec?.executedAt ?? exec?.date ?? exec?.t);
    if (tsSec == null || !Number.isFinite(tsSec)) continue;

    let markerTime = Math.floor(tsSec / tfSec) * tfSec;
    if (!Number.isFinite(markerTime)) continue;

    // Snap 16:00:00 MOC closing fills to maxCandle if within 1 bar of session end
    if (markerTime > maxCandle && (markerTime - maxCandle) <= tfSec) {
      markerTime = maxCandle;
    }

    if (markerTime < minCandle || markerTime > maxCandle) continue;

    const qty = toPositiveNumber(exec?.qty ?? exec?.quantity ?? exec?.shares);
    const signedQty = side === 'buy' ? qty : -qty;

    const prevPos = runningPosition;
    const nextPos = prevPos + signedQty;
    const prevSign = Math.sign(prevPos);
    const nextSign = Math.sign(nextPos);

    const isReversal = prevSign !== 0 && nextSign !== 0 && prevSign !== nextSign;
    const isStateOpen = prevPos === 0 && nextPos !== 0;
    const isStateClose = prevPos !== 0 && nextPos === 0;
    const isScaleIn =
      prevSign !== 0 &&
      nextSign === prevSign &&
      ((prevSign > 0 && signedQty > 0) || (prevSign < 0 && signedQty < 0)) &&
      Math.abs(nextPos) > Math.abs(prevPos);
    const isScaleOut =
      prevSign !== 0 &&
      nextSign === prevSign &&
      ((prevSign > 0 && signedQty < 0) || (prevSign < 0 && signedQty > 0)) &&
      Math.abs(nextPos) < Math.abs(prevPos);

    runningPosition = nextPos;

    const position: 'aboveBar' | 'belowBar' = side === 'buy' ? 'belowBar' : 'aboveBar';
    let shape: 'arrowUp' | 'arrowDown' | 'circle' = side === 'buy' ? 'arrowUp' : 'arrowDown';
    let color = side === 'buy' ? BUY_COLOR : SELL_COLOR;

    if (isScaleIn) {
      shape = 'circle';
      color = side === 'buy' ? SCALE_IN_BUY_COLOR : SCALE_IN_SELL_COLOR;
    }

    const isOpenLike = Boolean(exec?.isEntry ?? exec?.isOpen) || isStateOpen;
    const isCloseLike = Boolean(exec?.isExit ?? exec?.isClose) || isStateClose;
    const sideLabel = side === 'buy' ? 'BUY' : 'SELL';
    const lifecycleLabel = isOpenLike ? 'OPEN' : isCloseLike ? 'CLOSE' : sideLabel;

    const sideWord = side === 'buy' ? 'Buy' : 'Sell';
    let text = sideWord;

    let fullText = `${lifecycleLabel} ${qty}`;

    // Robust price extraction: try multiple fields commonly used across data sources
    const rawPriceCandidate = (
      (exec as any)?.price ?? (exec as any)?.avgPrice ?? (exec as any)?.fillPrice ?? (exec as any)?.entryPrice ?? (exec as any)?.exitPrice ?? (exec as any)?.entry_price ?? (exec as any)?.avg_price
    );
    const parsedPrice = Number(rawPriceCandidate);
    const hasExplicitPriceField = typeof rawPriceCandidate !== 'undefined' && rawPriceCandidate !== null && String(rawPriceCandidate).trim() !== '';
    const price = Number.isFinite(parsedPrice) ? parsedPrice : NaN;

    // Guard: do not treat missing/invalid price as 0. If no explicit price field, treat as absent.
    const usePrice = Number.isFinite(price) && (price !== 0 || hasExplicitPriceField);
    if (usePrice) {
      const formattedPrice = formatPriceForSymbol(price, options?.symbol, options?.secType);
      text = `${sideWord} @ ${formattedPrice}`;
      fullText += ` @ ${formattedPrice}`;
    }

    const candleRange = candleByTime.get(markerTime);
    if (Number.isFinite(price) && candleRange) {
      const tolerance = Math.max(0.0001, Math.abs(price) * 0.001);
      if (price < candleRange.low - tolerance || price > candleRange.high + tolerance) {
        fullText += ' ⚠ off-candle';
      }
    }

    if (isReversal) {
      color = REVERSAL_COLOR;
      fullText = `R ${fullText}`;
    }

    // Determine explicit action type for anchor-dot semantics (kept out of visible label)
    let actionType: ExecutionSeriesMarker['actionType'] = 'none';
    if (isOpenLike) actionType = 'open';
    else if (isScaleIn) actionType = 'scaleIn';
    else if (isScaleOut) actionType = 'scaleOut';
    else if (isCloseLike) actionType = 'close';

    const dedupeKey = `${markerTime}|${position}|${shape}|${color}`;
    const duplicateIndex = duplicateCounts.get(dedupeKey) ?? 0;
    duplicateCounts.set(dedupeKey, duplicateIndex + 1);

    if (duplicateIndex > 0) {
      shape = 'circle';
      fullText = `${fullText} #${duplicateIndex + 1}`;
    }

    const marker: ExecutionSeriesMarker = {
      time: markerTime,
      position,
      color,
      shape,
      text,
      executionId: String(exec?.id ?? exec?.storageId ?? exec?.executionId ?? exec?.orderId ?? `${tsSec}:${markers.length}`),
      executionTime: tsSec,
      executionPrice: Number.isFinite(price) ? price : undefined,
      fullText,
      side,
      actionType,
      // keep marker glyphs compact so labels remain readable
      size: 1,
    };

    markers.push(marker);
  }

  return markers.sort((a, b) => Number(a.time) - Number(b.time));
}
